import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Truck, Shield, CheckCircle, Loader2, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FittingCentrePicker from '../components/FittingCentrePicker';
import { computeSavings, promoSession, swapStore, PENDING_PROMO_KEY } from '../services/savings';
import { usePromoRevalidation } from '../hooks/usePromoRevalidation';
import { cartAPI, ordersAPI, authAPI, accountAPI, api, paymentsAPI, shippingAPI, promosAPI } from '../services/api';
import { ga } from '../services/ga';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';
import { useShippingSummary, customerCountry } from '../services/shippingInfo';
import { vehicleStore } from '../services/vehicleStore';
import CheckoutShippingStep from '../components/checkout/CheckoutShippingStep';
import CheckoutPaymentStep from '../components/checkout/CheckoutPaymentStep';
import CheckoutSummary from '../components/checkout/CheckoutSummary';
import CheckoutConfirmation, { CheckoutProgress } from '../components/checkout/CheckoutConfirmation';

const SHIP_REQUIRED = ['firstName', 'lastName', 'email', 'phone', 'address', 'postalCode', 'city'];
const EMPTY_BILLING = { company: '', vat_number: '', firstName: '', lastName: '', address: '', addressComplement: '', postalCode: '', city: '', country: 'France' };

const CheckoutPage = () => {
  const { t } = useT();
  usePageMeta({ title: t('checkout.stepPayment'), noindex: true });
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const [searchParams] = useSearchParams();
  const autoPay = searchParams.get('pay') === '1';
  const autoPayState = useRef({ done: false, lastOrderTried: false });
  const [orderComplete, setOrderComplete] = useState(false);
  const assist = authAPI.impersonation();
  const [orderId, setOrderId] = useState(null);
  const isAuthenticated = authAPI.isAuthenticated();
  const user = authAPI.getCurrentUser();

  const [promo, setPromoState] = useState(() => promoSession.get());
  const setPromo = (p) => { setPromoState(p); promoSession.set(p); };
  usePromoRevalidation(cart?.items, promo, setPromoState);
  const promoParam = (searchParams.get('promo') || sessionStorage.getItem(PENDING_PROMO_KEY) || '').trim();
  useEffect(() => {
    if (!promoParam || !isAuthenticated || promo?.code === promoParam.toUpperCase()) return;
    promosAPI.validate(promoParam).then((p) => { setPromoState(p); promoSession.set(p); sessionStorage.removeItem(PENDING_PROMO_KEY); }).catch(() => {});
  }, [promoParam, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  const [shippingInfo, setShippingInfo] = useState({
    firstName: user?.first_name || '', lastName: user?.last_name || '', email: user?.email || '',
    phone: '', address: '', addressComplement: '', postalCode: '', city: '', country: 'France',
  });

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [redirecting, setRedirecting] = useState(false);
  const [shipOptions, setShipOptions] = useState({ options: [], countries: [] });
  const [carrier, setCarrier] = useState(null);
  const [relayPoint, setRelayPoint] = useState(null);
  const [relayOpen, setRelayOpen] = useState(false);
  const cartSubtotal = (cart?.items || []).reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tyreQty = (cart?.items || []).reduce((sum, item) => sum + (item.is_tyre ? item.quantity * (item.units || 1) : 0), 0);
  const weightKg = Math.round((cart?.items || []).reduce((sum, item) => sum + (Number(item.weight_kg) || 0) * item.quantity * (item.units || 1), 0) * 100) / 100;
  useEffect(() => {
    let cancelled = false;
    shippingAPI.options(shippingInfo.country, cartSubtotal, tyreQty, weightKg).then((d) => {
      if (cancelled) return;
      setShipOptions(d);
      setCarrier((c) => (d.options.some((o) => o.carrier === c) ? c : d.options[0]?.carrier || null));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [shippingInfo.country, cartSubtotal, tyreQty, weightKg]);
  useEffect(() => { if (shippingInfo.country) customerCountry.set(shippingInfo.country); }, [shippingInfo.country]);
  const selectedShip = shipOptions.options.find((o) => o.carrier === carrier) || shipOptions.options[0] || null;
  const [formError, setFormError] = useState('');
  const [shortages, setShortages] = useState([]);
  const [orderNumber, setOrderNumber] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [wallet, setWallet] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [fittingCentreId, setFittingCentreId] = useState(null);
  const [fittingQuote, setFittingQuote] = useState(null);
  const [fittingSlot, setFittingSlot] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [proInfo, setProInfo] = useState(null);
  const [otherBilling, setOtherBilling] = useState(false);
  const [billing, setBilling] = useState(EMPTY_BILLING);
  const [billingAddressId, setBillingAddressId] = useState(null);
  const pickBillingAddress = (a) => { setBillingAddressId(a.id); setBilling(b => ({ ...b, firstName: a.first_name, lastName: a.last_name, address: a.address, addressComplement: a.complement || '', postalCode: a.postal_code, city: a.city, country: a.country || 'France' })); };
  const [vatRate, setVatRate] = useState(20);

  const retryPayment = async () => {
    setIsProcessing(true); setFormError('');
    try { setRedirecting(true); const pay = await paymentsAPI.sumupCheckout(orderId); window.location.assign(pay.checkout_url); }
    catch (e) { setRedirecting(false); setIsProcessing(false); setFormError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('checkout.errPayment')); }
  };

  const applyAddress = (a) => {
    setSelectedAddress(a.id);
    setShippingInfo(prev => ({ ...prev, firstName: a.first_name, lastName: a.last_name, phone: a.phone || prev.phone, address: a.address, addressComplement: a.complement || '', postalCode: a.postal_code, city: a.city, country: a.country || 'France' }));
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    accountAPI.addresses().then(list => {
      setAddresses(list);
      const def = list.find(a => a.is_default_shipping) || list[0];
      if (def) applyAddress(def);
      const bill = list.find(a => a.is_default_billing);
      if (bill && bill.id !== def?.id) { setOtherBilling(true); pickBillingAddress(bill); }
    }).catch(() => {});
    accountAPI.wallet().then(w => { setWallet(w.balance || 0); setUseWallet((w.balance || 0) > 0); }).catch(() => {});
    api.get('/company').then(r => setVatRate(r.data?.vat_rate ?? 20)).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/connexion?redirect=/checkout');
      return;
    }
    authAPI.getProfile().then(p => {
      setIsPro(!!p.is_pro);
      if (p.is_pro && p.pro) { setProInfo(p.pro); setBilling(b => ({ ...b, company: p.pro.company_name || '', vat_number: p.pro.vat_number || '' })); }
      setShippingInfo(prev => ({
        ...prev,
        firstName: prev.firstName || p.first_name || '',
        lastName: prev.lastName || p.last_name || '',
        phone: prev.phone || p.phone || '',
        address: prev.address || p.address?.address || '',
        addressComplement: prev.addressComplement || p.address?.addressComplement || '',
        postalCode: prev.postalCode || p.address?.postalCode || '',
        city: prev.city || p.address?.city || '',
      }));
    }).catch(() => {});
    const loadCart = async () => {
      try {
        const data = await cartAPI.get();
        if (!data.items || data.items.length === 0) {
          navigate('/panier');
          return;
        }
        setCart(data);
        ga.beginCheckout(data.items, promoSession.get()?.code);
      } catch (error) {
        console.error('Error loading cart:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCart();
  }, [isAuthenticated, navigate]);

  // E-mail « Finaliser et payer » (/checkout?pay=1) : adresse connue → saut direct à l'étape paiement, sinon dernière commande, sinon étape adresse
  useEffect(() => {
    if (!autoPay || isLoading || !cart || autoPayState.current.done || !shipOptions.options.length) return;
    if (SHIP_REQUIRED.every(f => shippingInfo[f]?.trim())) {
      autoPayState.current.done = true;
      setStep(2);
      return;
    }
    if (autoPayState.current.lastOrderTried) return;
    autoPayState.current.lastOrderTried = true;
    ordersAPI.getAll().then(list => {
      const last = (list || []).find(o => o.shipping_info?.address);
      if (!last) return;
      const a = last.shipping_info;
      setShippingInfo(prev => ({ ...prev, firstName: prev.firstName || a.firstName || '', lastName: prev.lastName || a.lastName || '', email: prev.email || a.email || '', phone: prev.phone || a.phone || '',
        address: prev.address || a.address || '', addressComplement: prev.addressComplement || a.addressComplement || '', postalCode: prev.postalCode || a.postalCode || '', city: prev.city || a.city || '', country: a.country || prev.country }));
    }).catch(() => {});
  }, [autoPay, isLoading, cart, shippingInfo, shipOptions.options.length]);

  const handleInputChange = (e) => setShippingInfo(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validateShipping = () => {
    const labels = {
      firstName: t('checkout.firstName'), lastName: t('checkout.lastName'), email: t('checkout.email'), phone: t('checkout.phone'),
      address: t('checkout.address'), postalCode: t('checkout.postalCode'), city: t('checkout.city')
    };
    for (const field of Object.keys(labels)) {
      if (!shippingInfo[field]?.trim()) {
        setFormError(t('checkout.errRequired', { field: labels[field] }));
        return false;
      }
    }
    if (!shipOptions.countries.length || !shipOptions.options.length) { setFormError(t('checkout.errCountry')); return false; }
    if (selectedShip?.relay && !relayPoint) { setFormError(t('checkout.relayRequired')); return false; }
    if (shippingInfo.country === 'France' ? !/^\d{5}$/.test(shippingInfo.postalCode) : !/^[A-Za-z0-9 -]{3,10}$/.test(shippingInfo.postalCode)) { setFormError(t('checkout.errPostal')); return false; }
    if (!/^[\w.-]+@[\w.-]+\.\w+$/.test(shippingInfo.email)) { setFormError(t('checkout.errEmail')); return false; }
    if (!/^(\+33|0)[1-9](\s?\d{2}){4}$/.test(shippingInfo.phone.trim())) { setFormError(t('checkout.errPhone')); return false; }
    if (otherBilling && (!billing.address.trim() || !/^\d{5}$/.test(billing.postalCode) || !billing.city.trim() || (!billing.company.trim() && (!billing.firstName.trim() || !billing.lastName.trim())))) {
      setFormError(t('checkout.errBilling'));
      return false;
    }
    setFormError('');
    return true;
  };

  const handleSubmitOrder = async () => {
    if (!validateShipping()) return;
    setIsProcessing(true);
    ga.addPaymentInfo(cart?.items || [], total, promo?.code, assist ? 'payment_link' : paymentMethod);
    try {
      const response = await ordersAPI.create({
        shipping_info: shippingInfo,
        payment_method: assist ? 'payment_link' : paymentMethod,
        use_wallet: useWallet && wallet > 0,
        promo_code: promo?.code || null,
        carrier: carrier || null,
        relay_point: selectedShip?.relay ? relayPoint : null,
        vehicle: vehicleStore.get(),
        fitting_centre_id: fittingCentreId || null,
        fitting_slot: fittingCentreId ? fittingSlot || null : null,
        billing_info: otherBilling ? billing : (isPro ? { ...shippingInfo, company: billing.company, vat_number: billing.vat_number } : null),
      });
      setOrderId(response.id);
      setOrderNumber(response.order_number);
      promoSession.clear();
      swapStore.clear();
      if (response.payment_status === 'pending' && !assist) {
        setRedirecting(true);
        const pay = await paymentsAPI.sumupCheckout(response.id);
        window.location.assign(pay.checkout_url);
        return;
      }
      setStep(3);
      setOrderComplete(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.shortages) {
        setShortages(detail.shortages);
        setFormError(detail.message);
      } else if (orderId || error.config?.url?.includes('/payments/sumup/')) {
        setFormError(typeof detail === 'string' ? detail : t('checkout.errPayment'));
        setRedirecting(false);
      } else {
        setFormError(typeof detail === 'string' ? detail : t('checkout.errOrder'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const subtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const promoDiscount = promo ? Math.min(promo.discount || 0, subtotal) : 0;
  const shipping = promo?.free_shipping ? 0 : (selectedShip ? selectedShip.price : 0);
  const fittingTotal = fittingQuote?.centres?.find((c) => c.id === fittingCentreId)?.total || 0;
  const total = Math.max(subtotal - promoDiscount + shipping + fittingTotal, 0);
  const shipInfo = useShippingSummary();
  const savings = computeSavings(cart.items, promo, shipInfo);
  const walletUsed = useWallet ? Math.min(wallet, total) : 0;
  const toPay = Math.max(total - walletUsed, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        <Footer />
      </div>
    );
  }

  if (orderComplete) {
    return <CheckoutConfirmation assist={assist} shippingInfo={shippingInfo} total={total} orderNumber={orderNumber} orderId={orderId} />;
  }

  const trustBadges = [
    { icon: Truck, label: t('checkout.fastDelivery'), sublabel: t('checkout.fastDeliverySub') },
    { icon: Shield, label: t('checkout.securePayment'), sublabel: t('checkout.sslSub') },
    { icon: CheckCircle, label: t('checkout.warranty'), sublabel: t('checkout.warrantySub') },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/panier" className="flex items-center gap-2 text-blue-600 hover:underline"><ArrowLeft size={18} />{t('checkout.backToCart')}</Link>
        </div>

        <CheckoutProgress step={step} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {step === 1 && (
              <CheckoutShippingStep
                shippingInfo={shippingInfo} onChange={handleInputChange} addresses={addresses} selectedAddress={selectedAddress} applyAddress={applyAddress} shipOptions={shipOptions}
                carrierProps={{ carrier, setCarrier, selectedShip, relayPoint, setRelayPoint, relayOpen, setRelayOpen }}
                billingProps={{ otherBilling, setOtherBilling, isPro, proInfo, billing, setBilling, billingAddressId, pickBillingAddress }}
                formError={formError} onContinue={() => validateShipping() && setStep(2)}
              />
            )}

            {step === 2 && autoPay && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-900 flex flex-wrap items-center justify-between gap-2" data-testid="checkout-autopay-notice">
                <span className="inline-flex items-center gap-2"><MapPin size={16} /> {t('checkout.autoPayNotice', { address: `${shippingInfo.address}, ${shippingInfo.postalCode} ${shippingInfo.city}` })}</span>
                <button type="button" onClick={() => setStep(1)} className="text-orange-700 underline font-medium" data-testid="checkout-autopay-edit">{t('checkout.autoPayEdit')}</button>
              </div>
            )}
            {step === 2 && <FittingCentrePicker value={fittingCentreId} onChange={setFittingCentreId} onQuote={setFittingQuote} postcode={shippingInfo.postalCode} city={shippingInfo.city} slot={fittingSlot} onSlotChange={setFittingSlot} />}
            {step === 2 && (
              <CheckoutPaymentStep
                assist={assist} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} toPay={toPay} total={total}
                formError={formError} orderId={orderId} orderComplete={orderComplete} retryPayment={retryPayment} isProcessing={isProcessing} redirecting={redirecting} shortages={shortages}
                onBack={() => setStep(1)} onSubmit={handleSubmitOrder}
              />
            )}

            <div className="mt-6 grid grid-cols-3 gap-4">
              {trustBadges.map((badge, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 text-center">
                  <badge.icon className="w-6 h-6 mx-auto text-orange-500 mb-2" />
                  <p className="text-sm font-medium text-gray-900">{badge.label}</p>
                  <p className="text-xs text-gray-500">{badge.sublabel}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <CheckoutSummary
              cart={cart} promo={promo} setPromo={setPromo} isProcessing={isProcessing} orderComplete={orderComplete}
              totals={{ subtotal, promoDiscount, shipping, fittingTotal, total, walletUsed, toPay }} selectedShip={selectedShip}
              isPro={isPro} vatRate={vatRate} savings={savings} wallet={wallet} useWallet={useWallet} setUseWallet={setUseWallet} shippingInfo={shippingInfo}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
